import { LoadScript, GoogleMap } from "@react-google-maps/api";
import { observer } from "mobx-react-lite";
import Image from "next/image";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  isChrome,
  isFirefox,
  isSafari,
  isEdge,
  isIE,
} from "react-device-detect";
import { FiArrowLeft, FiSearch } from "react-icons/fi";
import { Api } from "../../../../services/api";
import { useStores } from "../../../../stores";
import { CustomerAddressData } from "../../../../stores/cart-store/cart-store.model";
import {
  CoverageArea,
  mapCoverageAreaObjectToCoverageArea,
} from "../../../../stores/coverage-area-store/coverage-area-store.model";
import { PlaceDetailsResponse } from "../../../../utils/interfaces";
import { Button } from "../../../../components/button";
import { IconGeo } from "../../../../components/icons/icon-geo";
import { BaseModalDialog } from "../../../../components/modals/base-modal-dialog";
import { LocationPickerContext, LocationSource, ModalType } from "./context";
import { LocationSheet } from "./location-sheet";

export const MapPicker: React.FC = observer(() => {
  const { coverageAreaStore, cartStore } = useStores();
  const { setModalType, onSubmit } = useContext(LocationPickerContext);

  const api = useMemo(() => {
    const _api = new Api();
    _api.setup();
    return _api;
  }, []);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [lat, setLat] = useState<number>(
    cartStore?.savedCheckoutData?.customerAddress?.latitude ?? -6.2275781
  ); // default lat
  const [lng, setLng] = useState<number>(
    cartStore?.savedCheckoutData?.customerAddress?.longitude ?? 106.7954454
  ); // defualt lng
  const [customerData, setCustomerData] = useState<CustomerAddressData>(
    cartStore?.savedCheckoutData?.customerAddress
  );
  const [coverageArea, setCoverageArea] = useState<CoverageArea>(
    coverageAreaStore?.selectedCoverageArea
  );

  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [showLocationSheet, setShowLocationSheet] = useState<boolean>(false);
  const [isOutOfArea, setIsOutOfArea] = useState<boolean>(false);

  const handleSetData = useCallback((detailResponse: PlaceDetailsResponse) => {
    const { parsed_data } = detailResponse;
    if (parsed_data) {
      setCustomerData({
        address: `${parsed_data.name} ${parsed_data.formatted_address.replace(
          parsed_data.name,
          ""
        )}`,
        latitude: parsed_data?.geometry?.location?.lat,
        longitude: parsed_data?.geometry?.location?.lng,
        stateId: parsed_data?.state?.state_id,
        cityId: parsed_data?.city?.city_id,
        districtId: parsed_data?.district?.district_id,
        subdistrictId: parsed_data?.subdistrict?.subdistrict_id,
        zipcode: parsed_data?.zipcode?.zipcode,
        notes: null,
        placeId: parsed_data?.place_id,
      });
    }
  }, []);

  const getCurrentPlace = useCallback(async () => {
    // set coordinate from current address data
    if (
      coverageArea &&
      coverageArea?.locationSource === LocationSource.dropdown
    ) {
      // get first placeId from combination of city name and district name
      const result = await api.getPlaceSuggestions(
        `${coverageAreaStore?.selectedCity?.name} - ${coverageAreaStore?.selectedDistrict?.name}`
      );
      if (result?.predictions?.length) {
        // if found, use the place for default address data and map center
        const response = await api.getPlaceDetails(
          result?.predictions[0]?.place_id
        );
        handleSetData(response);
        setLat(response?.parsed_data?.geometry?.location?.lat);
        setLng(response?.parsed_data?.geometry?.location?.lng);
        if (response?.coverage_areas?.length) {
          setCoverageArea(
            mapCoverageAreaObjectToCoverageArea(response?.coverage_areas[0])
          );
        }
      }
      return;
    }

    if (map) {
      const { lat, lng } = map.getCenter();
      setLat(lat());
      setLng(lng());
      const { results } = await api.getPlaceId(lat(), lng());
      if (results?.length) {
        const response = await api.getPlaceDetails(results[0]?.place_id);
        handleSetData(response);
        if (response?.coverage_areas?.length) {
          if (isOutOfArea) setIsOutOfArea(false);
          setCoverageArea(
            mapCoverageAreaObjectToCoverageArea(response?.coverage_areas[0])
          );
        } else {
          setIsOutOfArea(true);
        }
      }
    }
  }, [
    api,
    coverageArea,
    coverageAreaStore?.selectedCity?.name,
    coverageAreaStore?.selectedDistrict?.name,
    handleSetData,
    isOutOfArea,
    map,
  ]);

  const getCurrentDeviceLocation = useCallback(async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const current = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          const { results } = await api.getPlaceId(current.lat, current.lng);
          if (results?.length) {
            const response = await api.getPlaceDetails(results[0].place_id);
            handleSetData(response);
            setLat(current.lat);
            setLng(current.lng);
            if (response?.coverage_areas?.length) {
              if (isOutOfArea) setIsOutOfArea(false);
              setCoverageArea(
                mapCoverageAreaObjectToCoverageArea(response?.coverage_areas[0])
              );
            } else {
              setIsOutOfArea(true);
            }
          }
        },
        (e) => {
          if (e.code === 1) {
            setTimeout(() => setShowHelpModal(true), 500);
            getCurrentPlace();
          }
        }
      );
    }
  }, [api, getCurrentPlace, handleSetData, isOutOfArea]);

  const handleSelectAddress = useCallback(
    async (placeId: string) => {
      const response = await api.getPlaceDetails(placeId);
      if (response) {
        const { parsed_data, coverage_areas } = response;
        handleSetData(response);
        if (parsed_data) {
          setLat(parsed_data.geometry.location.lat);
          setLng(parsed_data.geometry.location.lng);
        }
        if (coverage_areas?.length) {
          if (isOutOfArea) setIsOutOfArea(false);
          setCoverageArea(
            mapCoverageAreaObjectToCoverageArea(response?.coverage_areas[0])
          );
        } else {
          setIsOutOfArea(true);
        }
      }
    },
    [api, handleSetData, isOutOfArea]
  );

  // MOUNT EFFECT

  useEffect(() => {
    if (customerData) getCurrentPlace();
    else getCurrentDeviceLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoadMap = useCallback((map: google.maps.Map) => setMap(map), []);

  const handleSave = async () => {
    if (!coverageArea?.id) return;

    // set coverage area
    coverageAreaStore?.setSelectedCoverageArea({
      ...coverageArea,
      locationSource: LocationSource.map,
    });

    // set customer address
    const savedCheckoutData = await cartStore?.getSavedCheckoutData();
    cartStore?.saveCheckoutData({
      ...savedCheckoutData,
      deliveryType: "delivery",
      ...(customerData && { customerAddress: customerData }),
    });

    onSubmit();
  };

  const goToHelp = useCallback(() => {
    if (isChrome) {
      window.open(
        "https://support.google.com/chrome/answer/142065?co=GENIE.Platform%3DDesktop&hl=en&oco=1",
        "_blank"
      );
    }
    if (isFirefox) {
      window.open(
        "https://support.mozilla.org/en-US/kb/does-firefox-share-my-location-websites?redirectslug=does-firefox-share-my-location-web-sites&redirectlocale=en-US",
        "_blank"
      );
    }
    if (isSafari) {
      window.open(
        "https://support.apple.com/en-in/guide/mac-help/mh35873/mac#:~:text=use%20Location%20Services-,On%20your%20Mac%2C%20choose%20Apple%20menu%20%3E%20System%20Preferences%2C%20click,%26%20Privacy%2C%20then%20click%20Privacy.&text=pane%20for%20me-,Click%20Location%20Services.,it%20to%20use%20Location%20Services.",
        "_blank"
      );
    }
    if (isEdge) {
      window.open(
        "https://support.microsoft.com/en-us/microsoft-edge/location-and-privacy-in-microsoft-edge-31b5d154-0b1b-90ef-e389-7c7d4ffe7b04",
        "_blank"
      );
    }
    if (isIE) {
      window.open(
        "https://support.microsoft.com/en-us/topic/change-security-and-privacy-settings-for-internet-explorer-11-9528b011-664c-b771-d757-43a2b78b2afe",
        "_blank"
      );
    }
    setShowHelpModal(false);
  }, []);

  const renderMap = () => {
    return (
      <div className="relative h-60 mb-4">
        <div
          onClick={getCurrentDeviceLocation}
          className="absolute flex items-center justify-center bottom-28 right-2 w-10 h-10 rounded-full bg-white shadow z-10 cursor-pointer"
        >
          <Image
            src={`/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/current-location.svg`}
            alt="Current Location"
            width={24}
            height={24}
          />
        </div>
        <LoadScript googleMapsApiKey={process.env.NEXT_PUBLIC_MAP_API_KEY}>
          <GoogleMap
            onLoad={onLoadMap}
            mapContainerStyle={{ width: "100%", height: "100%" }}
            options={{
              mapTypeControl: false,
              fullscreenControl: false,
              streetViewControl: false,
            }}
            center={{ lat, lng }}
            zoom={15}
            onDragEnd={getCurrentPlace}
          >
            <div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-[30px] select-none b-0 p-0 m-0 bg-contain bg-no-repeat"
              style={{
                backgroundImage: `url("/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/map-marker.svg")`,
              }}
            />
          </GoogleMap>
        </LoadScript>
      </div>
    );
  };

  const renderAddressInput = () => {
    return (
      <div className="mt-6 mb-4">
        <div>
          <label htmlFor="address" className="text-gray font-bold">
            Titik Pengiriman
          </label>
          <div className="flex items-center border-b border-gray-300 cursor-text pb-2 mt-2">
            <FiSearch
              className="text-primary mr-1"
              id="map-picker-dialog-search-input"
              data-testid="map-picker-dialog-search-input"
            />
            <p className="flex-1 mb-0 truncate">{customerData?.address}</p>
            <div
              onClick={() => setShowLocationSheet(true)}
              className="shrink-0 cursor-pointer text-primary"
              id="map-picker-dialog-change-address-btn"
              data-testid="map-picker-dialog-change-address-btn"
            >
              Ubah
            </div>
          </div>
        </div>
        <div
          onClick={getCurrentDeviceLocation}
          className="text-primary cursor-pointer flex items-center text-xs"
          id="map-picker-dialog-current-location-btn"
          data-testid="map-picker-dialog-current-location-btn"
        >
          <IconGeo className="w-4 mr-1" />
          Gunakan lokasi sekarang
        </div>
      </div>
    );
  };

  const renderNotesArea = () => {
    return (
      <div className="mb-6">
        <label htmlFor="orderNotes" className="text-gray font-bold">
          Catatan (Opsional)
        </label>
        <textarea
          id="map-picker-dialog-notes-input"
          data-testid="map-picker-dialog-notes-input"
          placeholder="Tuliskan no rumah/petunjuk lokasi"
          value={customerData?.notes}
          onChange={(e) => {
            setCustomerData((prev) => ({ ...prev, notes: e.target.value }));
          }}
          rows={3}
          className="block w-full p-2 mt-3 mb-2 border outline-none focus:border-primary"
        />
      </div>
    );
  };

  const renderSaveBtn = () => {
    return (
      <Button
        onClick={handleSave}
        disabled={isOutOfArea}
        size="lg"
        className="w-full"
      >
        {isOutOfArea ? "AREA BELUM TERJANGKAU" : "SIMPAN"}
      </Button>
    );
  };

  const renderHelpModal = () => {
    return (
      <BaseModalDialog
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(null)}
        shouldCloseOnOverlayClick={false}
        title="Aktifkan Lokasi Kamu"
        titleClassName="text-center"
        className="max-w-xs"
        withoutInitialFocus
      >
        <p className="text-center mb-6">
          Layanan ini memerlukan izin akses lokasi dari browser kamu
        </p>
        <button
          onClick={goToHelp}
          className="w-full rounded py-2 text-white hover:opacity-95 bg-primary"
          id="map-picker-dialog-current-save-btn"
          data-testid="map-picker-dialog-current-save-btn"
        >
          CARA MENGAKTIFKAN
        </button>
      </BaseModalDialog>
    );
  };

  const renderLocationSheet = () => {
    if (!showLocationSheet) return;

    return (
      <LocationSheet
        visible={showLocationSheet}
        onDismiss={() => setShowLocationSheet(false)}
        onSelectAddress={handleSelectAddress}
      />
    );
  };

  return (
    <>
      <div className="flex items-center mb-4">
        <FiArrowLeft
          onClick={() => setModalType(ModalType.locationPicker)}
          className="cursor-pointer text-heading6 mr-2"
          id="map-picker-dialog-back-btn"
          data-testid="map-picker-dialog-back-btn"
        />
        <div className="text-label-xl font-bold">Pilih Alamat Pengiriman</div>
      </div>
      {renderMap()}
      {renderAddressInput()}
      {renderNotesArea()}
      {renderSaveBtn()}
      {renderHelpModal()}
      {renderLocationSheet()}
    </>
  );
});
